import React, { useEffect, useRef, useState } from 'react';
import './App.css';

function LazyVideo({ src, className, width, height, eager = false }) {
  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;

    video.muted = true;
    video.defaultMuted = true;

    let isNearViewport = false;
    const play = () => {
      if (isNearViewport) video.play().catch(() => {});
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        isNearViewport = entry.isIntersecting;
        if (entry.isIntersecting) {
          play();
        } else {
          video.pause();
        }
      },
      { rootMargin: '300px 0px' }
    );

    video.addEventListener('canplay', play);
    observer.observe(video);
    return () => {
      video.removeEventListener('canplay', play);
      observer.disconnect();
    };
  }, []);

  return (
    <video
      ref={videoRef}
      src={src}
      autoPlay
      loop
      muted
      playsInline
      controls={false}
      disablePictureInPicture
      preload={eager ? 'auto' : 'metadata'}
      width={width}
      height={height}
      className={className}
    />
  );
}

function useTimestamp() {
  const [ts, setTs] = useState('');

  useEffect(() => {
    const format = () => {
      const now = new Date();
      let hours = now.getHours();
      const mins = String(now.getMinutes()).padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12 || 12;
      return `${hours}:${mins} ${ampm}`;
    };
    setTs(format());
    const id = setInterval(() => setTs(format()), 1000);
    return () => clearInterval(id);
  }, []);

  return ts;
}

function useClickSound() {
  const audioContextRef = useRef(null);
  const noiseBufferRef = useRef(null);

  return () => {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;

    const context = audioContextRef.current || new AudioContext();
    audioContextRef.current = context;
    if (context.state === 'suspended') context.resume();

    if (!noiseBufferRef.current) {
      const frameCount = Math.round(context.sampleRate * 0.035);
      const buffer = context.createBuffer(1, frameCount, context.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < frameCount; i += 1) {
        data[i] = Math.random() * 2 - 1;
      }
      noiseBufferRef.current = buffer;
    }

    const now = context.currentTime;
    const source = context.createBufferSource();
    const filter = context.createBiquadFilter();
    const gain = context.createGain();
    const chirp = context.createOscillator();
    const chirpGain = context.createGain();

    source.buffer = noiseBufferRef.current;
    filter.type = 'bandpass';
    filter.frequency.value = 1800;
    filter.Q.value = 0.8;
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.022, now + 0.002);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.04);

    chirp.type = 'sine';
    chirp.frequency.setValueAtTime(280, now);
    chirp.frequency.exponentialRampToValueAtTime(520, now + 0.075);
    chirpGain.gain.setValueAtTime(0.0001, now);
    chirpGain.gain.exponentialRampToValueAtTime(0.055, now + 0.004);
    chirpGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.09);

    source.connect(filter).connect(gain).connect(context.destination);
    chirp.connect(chirpGain).connect(context.destination);
    source.start(now);
    source.stop(now + 0.04);
    chirp.start(now);
    chirp.stop(now + 0.09);
  };
}

function App() {
  const timestamp = useTimestamp();
  const playClick = useClickSound();

  return (
    <div
      className="App"
      onClick={playClick}
      onContextMenu={(event) => event.preventDefault()}
      onDragStart={(event) => event.preventDefault()}
    >
      <div className="main-layout">
        <div className="left-panel">
          <header className="site-header">
            <span className="header-name">Grayson</span>
            <span className="header-timestamp mobile-timestamp">{timestamp}</span>
          </header>
          <p className="left-title">Designer talking to agents all day in NYC.</p>
          <footer className="left-footer">
            <span className="footer-text">Designer & founder</span>
          </footer>
        </div>
        <div className="right-panel">
          <div className="masonry-grid">
            <div className="masonry-col">
              <div className="masonry-item phone-container waddle-container">
                <LazyVideo src="/images/waddle-optimized.mp4" className="phone-container-vid" width="464" height="1000" eager />
              </div>
              <div className="masonry-item">
                <LazyVideo src="/images/mobile-optimized.mp4" className="masonry-vid-full" width="960" height="540" />
              </div>
              <div className="masonry-item">
                <LazyVideo src="/images/desktopwarp-optimized.mp4" className="masonry-vid-full" width="960" height="540" />
              </div>
              <div className="masonry-item"><img src="/images/alphalabs-hero.jpg" alt="" width="2798" height="1600" loading="lazy" decoding="async" /></div>
            </div>
            <div className="masonry-col">
              <div className="masonry-item"><img src="/images/warp-billboard.png" alt="" width="1024" height="682" fetchPriority="high" decoding="async" /></div>
              <div className="masonry-item mock-block">
                <LazyVideo src="/images/roi-demo.mp4" className="mock-video" width="1356" height="1080" />
              </div>
              <div className="masonry-item phone-container">
                <img src="/images/roi-mock.png" alt="" width="472" height="1024" className="phone-container-vid roi-vid" loading="lazy" decoding="async" />
              </div>
            </div>
          </div>
          <span className="header-timestamp portfolio-timestamp">{timestamp}</span>
        </div>
      </div>

      <div className="noise-overlay" aria-hidden="true" />

      <footer className="site-footer mobile-footer">
        <span className="footer-text">Designer & founder</span>
      </footer>
      <img
        src="/images/waddle-hang-v2.png"
        alt=""
        className="waddle-peek"
        loading="lazy"
        decoding="async"
      />
    </div>
  );
}

export default App;
